import {
  createFrontendModule,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import {
  EntityContentBlueprint,
  EntityContentLayoutBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import RepositoryIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import BugReportIcon from '@mui/icons-material/BugReport';
import DnsIcon from '@mui/icons-material/Dns';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LayersIcon from '@mui/icons-material/Layers';
import { createElement } from 'react';
import { CatalogExplorerPage } from './CatalogExplorerPage';
import { PagerDutyTeamsExplorerPage } from './PagerDutyTeamsExplorerPage';
import { KaaPTenantExplorerPage } from '@internal/backstage-plugin-kaap-frontend';
import { DaaSExplorerPage } from '@internal/backstage-plugin-daas-frontend';
import { CatalogFeature } from './catalogFeatures';
import {
  groupColumns,
  userColumns,
  subscriptionColumns,
  jiraTeamColumns,
  kubernetesClusterColumns,
} from './catalogColumns';

const aboutRelationsLayout = EntityContentLayoutBlueprint.make({
  name: 'about-relations',
  params: {
    loader: async () => {
      const { AboutRelationsEntityContentLayout } = await import(
        './AboutRelationsEntityContentLayout'
      );
      return AboutRelationsEntityContentLayout;
    },
  },
});

const systemDiagramTab = EntityContentBlueprint.make({
  name: 'system-diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    filter: 'kind:system',
    loader: async () => {
      const { SystemDiagramContent } = await import('./SystemDiagramContent');
      return createElement(SystemDiagramContent);
    },
  },
});

export const daasExplorerPage = PageBlueprint.make({
  name: 'catalog-daas',
  params: {
    path: '/catalog/daas',
    title: 'Deployment as a Service',
    icon: createElement(LayersIcon),
    loader: async () => createElement(DaaSExplorerPage),
  },
});

const groupFeatures = [
  { [CatalogFeature.KindSelector]: false },
  { [CatalogFeature.TypeSelector]: false },
  CatalogFeature.Owned,
  CatalogFeature.OwnerSelector,
  CatalogFeature.BusinessProductUnitSelector,
];

const userFeatures = [
  { [CatalogFeature.KindSelector]: false },
  { [CatalogFeature.Owned]: false },
  CatalogFeature.BusinessProductUnitSelector,
];

export const catalogPagesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    PageBlueprint.make({
      name: 'catalog-groups',
      params: {
        path: '/catalog/groups',
        title: 'Catalog Groups',
        icon: createElement(GroupIcon),
        loader: async () =>
          createElement(CatalogExplorerPage, {
            initialKind: 'group',
            features: groupFeatures,
            columns: groupColumns,
          }),
      },
    }),
    PageBlueprint.make({
      name: 'catalog-users',
      params: {
        path: '/catalog/users',
        title: 'Catalog Users',
        icon: createElement(PersonIcon),
        loader: async () =>
          createElement(CatalogExplorerPage, {
            initialKind: 'user',
            features: userFeatures,
            columns: userColumns,
          }),
      },
    }),
    PageBlueprint.make({
      name: 'catalog-repositories',
      params: {
        path: '/catalog/repositories',
        title: 'Catalog Repositories',
        icon: createElement(RepositoryIcon),
        loader: async () =>
          createElement(CatalogExplorerPage, {
            initialKind: 'repository',
          }),
      },
    }),
    PageBlueprint.make({
      name: 'catalog-subscriptions',
      params: {
        path: '/catalog/subscriptions',
        title: 'Catalog Subscriptions',
        icon: createElement(CloudIcon),
        loader: async () =>
          createElement(CatalogExplorerPage, {
            initialKind: 'resource',
            initialType: 'subscription',
            columns: subscriptionColumns,
          }),
      },
    }),
    PageBlueprint.make({
      name: 'catalog-kubernetes-clusters',
      params: {
        path: '/catalog/kubernetes-clusters',
        title: 'Kubernetes Clusters',
        icon: createElement(DeviceHubIcon),
        loader: async () =>
          createElement(CatalogExplorerPage, {
            initialKind: 'resource',
            initialType: 'kubernetes-cluster',
            columns: kubernetesClusterColumns,
          }),
      },
    }),
    PageBlueprint.make({
      name: 'catalog-jira-teams',
      params: {
        path: '/catalog/jira-teams',
        title: 'Catalog Jira Teams',
        icon: createElement(BugReportIcon),
        loader: async () =>
          createElement(CatalogExplorerPage, {
            initialKind: 'component',
            initialType: 'jira-team',
            columns: jiraTeamColumns,
          }),
      },
    }),
    PageBlueprint.make({
      name: 'catalog-pagerduty-teams',
      params: {
        path: '/catalog/pagerduty-teams',
        title: 'Catalog PagerDuty Teams',
        icon: createElement(NotificationsActiveIcon),
        loader: async () => createElement(PagerDutyTeamsExplorerPage),
      },
    }),
    PageBlueprint.make({
      name: 'catalog-kaap-tenants',
      params: {
        path: '/catalog/kaap-tenants',
        title: 'KaaP Tenants',
        icon: createElement(DnsIcon),
        loader: async () => createElement(KaaPTenantExplorerPage),
      },
    }),
    daasExplorerPage,
    aboutRelationsLayout,
    systemDiagramTab,
  ],
});
